import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import QRCode from 'qrcode';
import type { CourseTeeSetData, EventScoreWithId, scoreHole } from '../../../shared/config/iTournamentBuilder';
import {
    SCORECARD_CANVAS_W,
    SCORECARD_CANVAS_H,
    resolveScorecardBindings,
    resolveTeeSet,
    resolveHighlightHoleNumber,
    visibleScorecardElements,
    scoringUnitInitialsLabel,
    playerNameToInitials,
} from './scorecardTemplateTypes';
import type { ScorecardElement, ScorecardTemplate, ScoringUnit } from './scorecardTemplateTypes';
import ScorecardGrid from './ScorecardGrid';

// ─── QR image (same pattern as ScoreCodeCardItem) ─────────────────────────────

function QrImage({ url, width, height }: { url: string; width: number; height: number }) {
    const [dataUrl, setDataUrl] = useState<string>('');

    useEffect(() => {
        if (!url) return;
        QRCode.toDataURL(url, { margin: 1, width: Math.round(Math.max(width, height) * 2) })
            .then(setDataUrl)
            .catch(() => setDataUrl(''));
    }, [url, width, height]);

    if (!dataUrl) return null;
    return (
        <img
            src={dataUrl}
            alt="QR Code"
            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
    );
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ScorecardCardItemProps {
    template: ScorecardTemplate;
    /** Merged per-score-code record produced by buildScoringUnits(). */
    scoringUnit: ScoringUnit;
    /** tbEvent cast to a loose map for binding resolution. */
    eventData: Record<string, unknown>;
    /** Course document used to resolve the tee set. Null when not yet loaded. */
    courseData: CourseTeeSetData | null;
    /**
     * EventScoreWithId for this scoring unit.
     * Pass null to render blank score rows (e.g. pre-round or preview-without-scores).
     */
    scores: EventScoreWithId | null;
    /**
     * Pre-resolved marker hole scores (from the marker's own roundScore.holeScores).
     * Null means the marker row is left blank even if showMarker is true in the template.
     */
    markerScores: scoreHole[] | null;
    /** Display name of the marker player (empty string if unknown). */
    markerLabel: string;
    /** Score code of the marker player; resolves {{markerScoreCode}} text bindings. */
    markerScoreCode?: string;
    /** 1-based round number used for tee time lookup and score extraction. */
    roundValue: number;
    /** When true, registration-level bindings and labels are suppressed. */
    blankMode?: boolean;
    /** Display width in px. Height is derived from the 11:4.25 aspect ratio. */
    displayWidth?: number;
    /** When true renders a thin border (useful in grid/preview view). */
    showBorder?: boolean;
    /** Optional ref forwarded to the outer card div (used for PDF capture). */
    cardRef?: React.RefObject<HTMLDivElement>;
}

// ─── Element renderer ─────────────────────────────────────────────────────────

interface ElementRendererProps {
    element: ScorecardElement;
    scoringUnit: ScoringUnit;
    eventData: Record<string, unknown>;
    courseData: CourseTeeSetData | null;
    scores: EventScoreWithId | null;
    markerScores: scoreHole[] | null;
    markerLabel: string;
    markerScoreCode: string;
    roundValue: number;
    blankMode: boolean;
    highlightHoleNumber: number | null;
    cardWidth: number;
    cardHeight: number;
}

function ScorecardElementRenderer({
    element,
    scoringUnit,
    eventData,
    courseData,
    scores,
    markerScores,
    markerLabel,
    markerScoreCode,
    roundValue,
    blankMode,
    highlightHoleNumber,
    cardWidth,
    cardHeight,
}: ElementRendererProps) {
    const left   = (element.x      / 100) * cardWidth;
    const top    = (element.y      / 100) * cardHeight;
    const width  = (element.width  / 100) * cardWidth;
    const height = (element.height / 100) * cardHeight;
    const scaleFactor = cardWidth / SCORECARD_CANVAS_W;

    const posStyle: React.CSSProperties = {
        position: 'absolute',
        left,
        top,
        width,
        height,
        overflow: 'hidden',
        boxSizing: 'border-box',
    };

    const regRecord: Record<string, unknown> = {
        ...(scoringUnit.registrations[0] as Record<string, unknown>),
        playerName:  scoringUnit.playerName,
        teamName:    scoringUnit.playerName,
        scoreCode:   scoringUnit.scoreCode,
    };

    // ── text ──────────────────────────────────────────────────────────────────
    if (element.type === 'text') {
        const resolved = resolveScorecardBindings(
            element.content ?? '',
            regRecord,
            eventData,
            roundValue,
            { blankRegistrationData: blankMode, markerName: markerLabel, markerScoreCode },
        );
        return (
            <div style={posStyle}>
                <Typography
                    component="span"
                    sx={{
                        fontFamily: 'Fredoka, sans-serif',
                        fontSize: (element.fontSize ?? 12) * scaleFactor,
                        fontWeight: element.fontWeight ?? 'normal',
                        color: element.color ?? '#000000',
                        textAlign: element.textAlign ?? 'left',
                        lineHeight: 1.3,
                        display: 'block',
                        width: '100%',
                        wordBreak: 'break-word',
                    }}
                >
                    {resolved}
                </Typography>
            </div>
        );
    }

    // ── image ─────────────────────────────────────────────────────────────────
    if (element.type === 'image') {
        if (!element.imageDataUrl) return null;
        return (
            <div style={posStyle}>
                <img
                    src={element.imageDataUrl}
                    alt=""
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                />
            </div>
        );
    }

    // ── qrcode ────────────────────────────────────────────────────────────────
    if (element.type === 'qrcode') {
        if (blankMode) return null;
        const base = process.env.REACT_APP_LIVE_SCORING_URL ?? '';
        const qrCode = element.qrTarget === 'marker' && markerScoreCode
            ? markerScoreCode
            : scoringUnit.scoreCode;
        const url = `${base}?scoreCode=${encodeURIComponent(qrCode)}&eventId=${encodeURIComponent(String(eventData._id ?? ''))}`;
        return (
            <div style={posStyle}>
                <QrImage url={url} width={width} height={height} />
            </div>
        );
    }

    // ── line ──────────────────────────────────────────────────────────────────
    if (element.type === 'line') {
        const color     = element.lineColor ?? '#000000';
        const thickness = (element.lineThickness ?? 1) * scaleFactor;
        const isH = element.lineOrientation !== 'vertical';

        const lineStyle: React.CSSProperties = isH
            ? {
                  width: '100%',
                  height: Math.max(thickness, 1),
                  backgroundColor: color,
              }
            : {
                  width: Math.max(thickness, 1),
                  height: '100%',
                  backgroundColor: color,
              };

        return (
            <div style={{ ...posStyle, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={lineStyle} />
            </div>
        );
    }

    // ── scorecardGrid ─────────────────────────────────────────────────────────
    if (element.type === 'scorecardGrid') {
        const cfg = element.gridConfig;
        if (!cfg) return null;

        const teeSet = courseData
            ? resolveTeeSet({ teeSet: scoringUnit.teeSet }, courseData)
            : null;

        const roundScores = scores?.roundScores?.filter(
            (rs) => rs.roundNumber === roundValue,
        ) ?? [];

        const roundScore = roundScores.find(
            (r) => r.playerScoreCode === scoringUnit.scoreCode,
        );
        const unitHoleScores: scoreHole[] = roundScore?.holeScores ?? [];
        const playerCount = cfg.playerRows === 'auto' ? 1 : (cfg.playerRows as number);
        const primaryLabel = blankMode ? '' : scoringUnitInitialsLabel(scoringUnit);
        const playerLabels = Array.from({ length: playerCount }, (_, i) =>
            i === 0 ? primaryLabel : '',
        );
        const playerScores: scoreHole[][] = Array.from({ length: playerCount }, (_, i) =>
            i === 0 ? unitHoleScores : [],
        );
        // Grid row uses initials (mirrors player row); full name stays in the token resolver above
        const effectiveMarkerLabel = blankMode ? '' : markerLabel.split(' / ').map(playerNameToInitials).filter(Boolean).join(' / ');
        const effectiveMarkerScores = blankMode ? [] : (markerScores ?? []);

        return (
            <div style={posStyle}>
                <ScorecardGrid
                    config={cfg}
                    teeSet={teeSet}
                    playerScores={playerScores}
                    playerLabels={playerLabels}
                    markerScores={effectiveMarkerScores}
                    markerLabel={effectiveMarkerLabel}
                    playerCount={playerCount}
                    scaleFactor={scaleFactor}
                    isSampleData={false}
                    highlightHoleNumber={highlightHoleNumber}
                    blankMode={blankMode}
                />
            </div>
        );
    }

    return null;
}

// ─── ScorecardCardItem ────────────────────────────────────────────────────────

export const ScorecardCardItem = React.forwardRef<HTMLDivElement, ScorecardCardItemProps>(
    function ScorecardCardItem(
        {
            template,
            scoringUnit,
            eventData,
            courseData,
            scores,
            markerScores,
            markerLabel,
            markerScoreCode,
            roundValue,
            blankMode = false,
            displayWidth = SCORECARD_CANVAS_W,
            showBorder = false,
            cardRef,
        },
        ref,
    ) {
        const cardWidth  = displayWidth;
        const cardHeight = Math.round((displayWidth * SCORECARD_CANVAS_H) / SCORECARD_CANVAS_W);

        const regRecord: Record<string, unknown> = {
            ...(scoringUnit.registrations[0] as Record<string, unknown>),
            playerName: scoringUnit.playerName,
            scoreCode:  scoringUnit.scoreCode,
        };
        const highlightHoleNumber = resolveHighlightHoleNumber(
            template,
            regRecord,
            eventData,
            roundValue,
        );

        const elements = visibleScorecardElements(template.elements);

        return (
            <Box
                ref={cardRef ?? ref}
                sx={{
                    position: 'relative',
                    width: cardWidth,
                    height: cardHeight,
                    backgroundColor: template.backgroundColor,
                    border: showBorder ? '1px solid rgba(0,0,0,0.15)' : 'none',
                    borderRadius: showBorder ? 1 : 0,
                    overflow: 'hidden',
                    flexShrink: 0,
                    boxSizing: 'border-box',
                }}
            >
                {elements.map((el) => (
                    <ScorecardElementRenderer
                        key={el.id}
                        element={el}
                        scoringUnit={scoringUnit}
                        eventData={eventData}
                        courseData={courseData}
                        scores={scores}
                        markerScores={markerScores}
                        markerLabel={markerLabel}
                        markerScoreCode={markerScoreCode ?? ''}
                        roundValue={roundValue}
                        blankMode={blankMode}
                        highlightHoleNumber={highlightHoleNumber}
                        cardWidth={cardWidth}
                        cardHeight={cardHeight}
                    />
                ))}
            </Box>
        );
    },
);
